import {
  Box,
  BoxProps,
  Image,
  ProgressBar,
  Text,
  useStyle,
} from '@betty/moonlight';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { Pressable, ViewStyle } from 'react-native';
import useStatus from '../hooks/useStatus';
import {
  statusBackgroundTokenMap,
  statusBadgeMap,
  statusTextTokenMap,
} from '../content/status.content';
import {
  BottomSheet,
  StackNavigationProp,
  useNavigation,
} from '@betty/navigation';
import {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  Easing,
} from 'react-native-reanimated';

function StatusHeaderItem(props: BoxProps) {
  const { status, pendingRewardsCount } = useStatus();
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const prevPendingRewardsCount = useSharedValue(pendingRewardsCount);
  const boxImageName = useMemo(
    () =>
      status?.pendingRewards.length
        ? 'common.BettyBoxLockedRotating'
        : 'common.BettyBoxLocked',
    [status?.pendingRewards.length],
  );

  const handleClick = useCallback(() => {
    if (status) {
      BottomSheet.open({
        namespace: 'status',
        type: status?.pendingRewards.length ? 'statusBoxes' : 'statusInfo',
        borderGradient: statusBackgroundTokenMap[status.playerStatus],
        borderStyle: 'allsides',
        headerImageName: statusBadgeMap[status.playerStatus],
        onInfo: () => navigation.navigate('StatusTerms'),
        scrollable: true,
      });
    }
  }, [status, navigation]);

  // Animations
  const gradientOffset = useSharedValue(0);
  const gradientOpacity = useSharedValue(0);
  const giftScale = useSharedValue(1);

  useEffect(() => {
    if (
      prevPendingRewardsCount.value !== undefined &&
      pendingRewardsCount &&
      pendingRewardsCount > prevPendingRewardsCount.value
    ) {
      gradientOffset.value = withSequence(
        withTiming(-2, { duration: 400, easing: Easing.inOut(Easing.ease) }),
        withTiming(-2, { duration: 1200 }),
        withTiming(0, { duration: 400, easing: Easing.inOut(Easing.ease) }),
      );

      gradientOpacity.value = withSequence(
        withTiming(1, { duration: 400, easing: Easing.inOut(Easing.ease) }),
        withTiming(1, { duration: 1200 }),
        withTiming(0, { duration: 400, easing: Easing.inOut(Easing.ease) }),
      );

      giftScale.value = withSequence(
        withTiming(1.5, { duration: 400, easing: Easing.inOut(Easing.ease) }),
        withTiming(1.5, { duration: 1200 }),
        withTiming(1, { duration: 400, easing: Easing.inOut(Easing.ease) }),
      );
    }

    prevPendingRewardsCount.value = pendingRewardsCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRewardsCount]);

  const gradientStyle = useAnimatedStyle(() => ({
    top: gradientOffset.value,
    bottom: gradientOffset.value,
    left: gradientOffset.value,
    right: gradientOffset.value,
    opacity: gradientOpacity.value,
  }));

  const giftboxStyle = useAnimatedStyle(() => ({
    transform: [{ scale: giftScale.value }],
  }));

  const pressableStyle = useStyle<ViewStyle>({
    height: 'space-1000',
    padding: 'space-200',
    gap: 'space-100',
    justifyContent: 'center',
  });

  if (!status) {
    // TODO: Return proper skeleton
    return (
      <Box
        height="space-1000"
        variant="default"
        backgroundColor="secondaryBackgroundDefault"
        borderRadius="radius-200"
        position="relative"
        {...props}
      />
    );
  }

  return (
    <Box
      height="space-1000"
      variant="default"
      backgroundColor="secondaryBackgroundDefault"
      borderRadius="radius-200"
      position="relative"
      marginRight="space-200"
      zIndex={1}
      {...props}
    >
      <Box
        variant="gradient"
        position="absolute"
        borderRadius="radius-200"
        zIndex={-1}
        gradient={statusBackgroundTokenMap[status.playerStatus]}
        animatedStyle={gradientStyle}
      />
      <Pressable onPress={handleClick} style={pressableStyle}>
        <Box gap="space-100" position="relative">
          <Text
            locNS="status"
            locKey={status.playerStatus}
            variant="nav-label"
            numberOfLines={1}
            gradient={statusTextTokenMap[status.playerStatus]}
          />
          <ProgressBar
            level={status.pendingRewards.length}
            from={0}
            to={status.totalLoyaltyPoints}
            progress={status.accumulatedLoyaltyPoints}
            gradientType={statusBackgroundTokenMap[status.playerStatus]}
          />
        </Box>
        <Box
          position="absolute"
          right="-space-400"
          animatedStyle={giftboxStyle}
        >
          <Image
            name={boxImageName}
            cachePolicy="memory"
            width="space-800"
            height="space-800"
          />
          {status?.pendingRewards.length ? (
            <Box
              position="absolute"
              top="space-0"
              right="space-0"
              width={14}
              height={14}
              variant="gradient"
              gradient="attentionBackground"
              justifyContent="center"
              alignItems="center"
              borderRadius="radius-full"
            >
              <Text variant="badge" color="darkSurfacePrimary">
                {status.pendingRewards.length}
              </Text>
            </Box>
          ) : null}
        </Box>
      </Pressable>
    </Box>
  );
}

export default memo(StatusHeaderItem);
